import React from 'react'
import dayjs from 'dayjs'
import { snakeCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Box as Column,
  DataListItem,
  Box as Heading,
  Box as MainArea,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { DataListItemCustom } from 'pages/Wallets/components/Table/SidePanel/DataListCustom'
import { PaymentsResponse } from 'types/payments'
import { shortenString } from 'utils/utils'

interface PaymentDetailsProps {
  transaction: PaymentsResponse
}

const PaymentDetails = ({ transaction }: PaymentDetailsProps) => {
  const { t } = useTranslation()

  const amountDue = transaction?.walletCurrency
    ? transaction.paidCurrency?.amount - transaction.paidCurrency?.actual
    : 0

  const amount = `${transaction?.displayCurrency?.amount?.toString()} ${transaction?.displayCurrency?.currency}`
  const amountPaid = `${transaction?.displayCurrency?.actual?.toString()} ${transaction?.displayCurrency?.currency}`
  const walletAmount = `${transaction?.walletCurrency?.amount?.toString()} ${transaction?.walletCurrency?.currency}`
  const walletNetAmount = `${transaction?.walletCurrency?.actual?.toString()} ${transaction?.walletCurrency?.currency}`

  const dataColumns = [
    {
      heading: `${t('transactionDetails')}:`,
      items: [
        {
          id: 'amount',
          testId: `details-${snakeCase(transaction?.reference)}-amount`,
          label: t('amount'),
          value: amount,
        },
        {
          id: 'paid',
          testId: `details-${snakeCase(transaction?.reference)}-paid`,
          label: t('paid'),
          value: amountPaid,
        },
        {
          id: 'walletAmount',
          testId: `details-${snakeCase(transaction?.reference)}-walletAmount`,
          label: t('walletAmount'),
          value: walletAmount,
        },
        {
          id: 'walletNetAmount',
          testId: `details-${snakeCase(transaction?.reference)}-walletNetAmount`,
          label: t('walletNetAmount'),
          value: walletNetAmount,
        },
        {
          id: 'createdDate',
          testId: `details-${snakeCase(transaction?.reference)}-createdDate`,
          label: t('createdDate'),
          value: dayjs(transaction?.dateCreated).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
          id: 'expiryDate',
          testId: `details-${snakeCase(transaction?.reference)}-expiryDate`,
          label: t('expiryDate'),
          value: dayjs(transaction?.expiryDate).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
          id: 'reference',
          testId: `details-${snakeCase(transaction?.reference)}-reference`,
          label: t('reference'),
          value: shortenString(transaction?.reference, 25),
          copyText: transaction?.reference,
        },
        {
          id: 'merchant',
          testId: `details-${snakeCase(transaction?.reference)}-merchant`,
          label: t('merchant'),
          value: transaction?.merchantDisplayName,
        },

        {
          id: 'type',
          testId: `details-${snakeCase(transaction?.reference)}-type`,
          label: t('type'),
          value: transaction?.type,
        },
        {
          id: 'uuid',
          testId: `details-${snakeCase(transaction?.reference)}-uuid`,
          label: t('Uuid'),
          value: ellipseMiddleText(transaction?.uuid, 6, 6),
          copyText: transaction?.uuid,
        },
        {
          id: 'hash',
          testId: `details-${snakeCase(transaction?.reference)}-hash`,
          label: t('hash'),
          values: transaction?.transactions?.map(transaction => transaction?.hash),
        },
      ],
    },
    {
      heading: `${t('quote')}:`,
      items: [
        {
          id: 'from',
          testId: `details-${snakeCase(transaction?.reference)}-from`,
          label: t('from'),
          value: transaction?.exchangeRate?.base,
        },
        {
          id: 'amountIn',
          testId: `details-${snakeCase(transaction?.reference)}-amountIn`,
          label: t('amountIn'),
          value: `${transaction?.walletCurrency?.actual?.toString()} ${transaction?.walletCurrency?.currency ?? ''}`,
        },
        {
          id: 'to',
          testId: `details-${snakeCase(transaction?.reference)}-to`,
          label: t('to'),
          value: transaction?.exchangeRate?.counter,
        },
        {
          id: 'amountDue',
          testId: `details-${snakeCase(transaction?.reference)}-amountDue`,
          label: t('amountDue'),
          value: `${amountDue?.toString()} ${transaction?.paidCurrency?.currency ?? ''}`,
        },
        {
          id: 'exchangeRate',
          testId: `details-${snakeCase(transaction?.exchangeRate?.rate?.toString())}-exchangeRate`,
          label: t('exchangeRate'),
          value: `1 ${transaction?.exchangeRate?.base} = ${transaction?.exchangeRate?.rate?.toString()} ${transaction?.exchangeRate?.counter ?? ''}`,
        },
        {
          id: 'expiryDate',
          testId: `details-${snakeCase(transaction?.reference)}-expiryDate`,
          label: t('expiryDate'),
          value: dayjs(transaction?.expiryDate).format('YYYY-MM-DD HH:mm:ss'),
        },
      ],
    },
  ]

  return (
    <MainArea className="space-y-6">
      {!!dataColumns?.length &&
        dataColumns?.map(column => {
          return (
            <Column key={column?.heading} flex flexCol className="xs:w-4/6 sm:w-full">
              <Heading flex className="mb-3">
                <h4 className="text-h4 font-medium text-foreground-prominent">{column?.heading}</h4>
              </Heading>

              {!!column?.items?.length &&
                column?.items?.map(({ label, value, values, id, testId, copyText }) => {
                  return id === 'hash' ? (
                    <DataListItemCustom
                      id="hash"
                      testId="hash"
                      title={t('hash')}
                      textList={values}
                    />
                  ) : (
                    <DataListItem
                      id={id}
                      key={id}
                      testId={testId}
                      title={label}
                      body={value}
                      copyText={copyText}
                      layout={'horizontal'}
                    />
                  )
                })}
            </Column>
          )
        })}
    </MainArea>
  )
}

export default React.memo(PaymentDetails)
